import { format } from "date-fns"
import { AlertCircle, CircleDot, History, ReceiptText } from "lucide-react"

import { type SubOrder, type SubOrderStatusHistoryDto } from "@/api/api-client"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { useLanguage } from "@/hooks/useLanguage"
import { useVendorSubOrderHistory } from "@/hooks/useOrders"

import type { VendorOrderAuditHistoryLabels } from "./vendor-order-types"

interface VendorOrderAuditHistorySheetProps {
  subOrder: SubOrder
  open: boolean
  onOpenChange: (open: boolean) => void
  statusLabels: Partial<Record<string, string>>
  dateFormat: string
}

export function VendorOrderAuditHistorySheet({
  subOrder,
  open,
  onOpenChange,
  statusLabels,
  dateFormat,
}: VendorOrderAuditHistorySheetProps) {
  const { t, language } = useLanguage()
  const labels = t.vendorOrders.auditHistory
  const { data, isLoading, isError } = useVendorSubOrderHistory(subOrder.id, open)
  const history = data ?? []

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex h-full w-full flex-col overflow-hidden border-l border-border bg-background p-0 shadow-2xl sm:max-w-[560px]">
        <SheetHeader className="border-b bg-muted/5 p-6 text-left">
          <div className="flex items-center gap-3">
            <History className="h-5 w-5 text-primary" aria-hidden="true" />
            <SheetTitle className="font-display text-2xl uppercase tracking-widest">
              {labels.title}
            </SheetTitle>
          </div>
          <SheetDescription>{labels.description}</SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {isLoading && (
            <div className="flex h-40 items-center justify-center text-sm font-medium text-muted-foreground">
              {labels.loading}
            </div>
          )}

          {isError && (
            <div className="flex h-40 flex-col items-center justify-center gap-3 text-center text-sm text-muted-foreground">
              <AlertCircle className="h-8 w-8 text-destructive" aria-hidden="true" />
              <span>{labels.error}</span>
            </div>
          )}

          {!isLoading && !isError && history.length === 0 && (
            <div className="flex h-40 items-center justify-center text-sm font-medium text-muted-foreground">
              {labels.empty}
            </div>
          )}

          {!isLoading && !isError && history.length > 0 && (
            <ol className="space-y-0">
              {history.map((entry, index) => (
                <AuditHistoryItem
                  key={entry.id}
                  entry={entry}
                  isLast={index === history.length - 1}
                  labels={labels}
                  statusLabels={statusLabels}
                  dateFormat={dateFormat}
                  language={language}
                />
              ))}
            </ol>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

function AuditHistoryItem({
  entry,
  isLast,
  labels,
  statusLabels,
  dateFormat,
  language,
}: {
  entry: SubOrderStatusHistoryDto
  isLast: boolean
  labels: VendorOrderAuditHistoryLabels
  statusLabels: Partial<Record<string, string>>
  dateFormat: string
  language: "es" | "en"
}) {
  const fromStatus = statusLabel(entry.fromStatus, statusLabels, labels.notAvailable)
  const requestedStatus = statusLabel(entry.requestedStatus, statusLabels, labels.notAvailable)
  const toStatus = statusLabel(entry.toStatus, statusLabels, labels.notAvailable)
  const actorType = localizeToken(entry.actorType, labels.actorTypes)
  const actorUser = entry.actorDisplayName || entry.actorUserId
  const source = localizeToken(entry.source, labels.sources)
  const reason = entry.reason ? localizeToken(entry.reason, labels.reasons) : undefined
  const changedAt = entry.changedAtUtc
    ? format(new Date(entry.changedAtUtc), `${dateFormat} HH:mm`)
    : labels.notAvailable

  return (
    <li className="grid grid-cols-[24px_1fr] gap-4">
      <div className="flex flex-col items-center">
        <CircleDot className="h-5 w-5 text-primary" aria-hidden="true" />
        {!isLast && <div className="mt-2 h-full w-px bg-border" />}
      </div>
      <div className="pb-7">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-bold text-foreground">
            {fromStatus === toStatus ? toStatus : `${fromStatus} -> ${toStatus}`}
          </span>
          <span className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
            {changedAt}
          </span>
        </div>

        {entry.requestedStatus !== entry.toStatus && (
          <p className="mt-1 text-xs font-medium text-muted-foreground">
            {labels.requested}: {requestedStatus}
          </p>
        )}

        {reason && <p className="mt-2 text-sm text-muted-foreground">{reason}</p>}

        <div className="mt-3 flex flex-wrap gap-2 text-xs font-medium text-muted-foreground">
          <span className="border border-border px-2 py-1">
            {labels.actor}: {actorType}
          </span>
          {actorUser && (
            <span className="border border-border px-2 py-1">
              {labels.actorUser}: {actorUser}
            </span>
          )}
          <span className="border border-border px-2 py-1">
            {labels.source}: {source}
          </span>
          {entry.shippingCost !== undefined && entry.shippingCost !== null && (
            <span className="border border-border px-2 py-1">
              {labels.shipping}: {formatMoney(entry.shippingCost, language)}
            </span>
          )}
          {entry.paymentId !== undefined && entry.paymentId !== null && (
            <span className="inline-flex items-center gap-1 border border-border px-2 py-1">
              <ReceiptText className="h-3.5 w-3.5" aria-hidden="true" />
              {labels.payment} #{entry.paymentId}
            </span>
          )}
        </div>
      </div>
    </li>
  )
}

function statusLabel(
  status: string | undefined,
  statusLabels: Partial<Record<string, string>>,
  fallback: string
) {
  if (!status) return fallback
  return statusLabels[status] || status
}

function localizeToken(value: string | undefined, labels: Partial<Record<string, string>>) {
  if (!value) return ""
  return labels[value] || value
}

function formatMoney(value: number, language: "es" | "en") {
  return new Intl.NumberFormat(language === "es" ? "es-MX" : "en-US", {
    style: "currency",
    currency: "MXN",
  }).format(value)
}
